<script setup>
import { useWebStore } from '~/store/web'
import MachineryIcon from '~/components/MachineryIcon.vue'

const props = defineProps({
  icon: { type: String, required: true },
  title: { type: String, required: true },
  text: { type: String, default: '' },
  buttonText: { type: String, default: '' },
  // Nyelv nélküli útvonal, pl. "egyedi-gepgyartas/egyedi-tartalyok"
  to: { type: String, default: '' },
  // Keskeny kártyához (4 oszlopos pillér-rács): az ikon a szöveg fölé kerül
  // (különben a cím 30-40 px széles hasábba szorulna), és a látványterv
  // szerint a kártya tartalma középre zárt.
  stacked: { type: Boolean, default: false },
})

const store = useWebStore()
</script>

<template>
  <div
    class="machinery-card group relative overflow-hidden bg-white deep-shadow rounded-2xl p-6 flex flex-col gap-4 h-full"
    :class="stacked ? 'text-center' : 'text-left'"
  >
    <!-- Hoverre kifutó narancs vonal a kártya tetején. -->
    <span class="card-accent" aria-hidden="true" />

    <div class="flex items-start gap-4" :class="stacked ? 'flex-col items-center gap-3' : ''">
      <span class="icon-badge shrink-0 grid place-items-center w-16 h-16 rounded-xl text-orange-500 group-hover:text-white">
        <MachineryIcon :name="icon" />
      </span>
      <div>
        <h3 class="InterExtraBold text-lg leading-snug lg:text-xl">{{ title }}</h3>
        <p v-if="text" class="InterMedium text-base mt-2 opacity-90">{{ text }}</p>
      </div>
    </div>

    <!-- Nincs `unstyled-link`: az felülírná a betűszínt és elvenné a kézkurzort. -->
    <NuxtLink
      v-if="buttonText && to"
      :to="'/' + store.chosenLang + '/' + to"
      class="card-button mt-auto w-max bg-orange InterMedium px-5 py-2.5 rounded-md no-underline inline-flex items-center gap-2"
      :class="stacked ? 'mx-auto' : ''"
    >
      {{ buttonText }}
      <span class="card-arrow" aria-hidden="true">→</span>
    </NuxtLink>
  </div>
</template>

<style scoped>
/* Scoped attribútum-selector: felülírja a globalStyle későbbi szabályait is. */
.card-button {
  color: white;
  cursor: pointer;
  background: rgb(255, 118, 24);
  transition: background-color .25s ease, box-shadow .25s ease;
}
.card-button:hover {
  background: #ef6100;
  box-shadow: 0 6px 18px rgba(255, 118, 24, .35);
}
.card-arrow {
  transition: transform .25s cubic-bezier(.22, .61, .36, 1);
}
.card-button:hover .card-arrow {
  transform: translateX(4px);
}

.machinery-card {
  /* Az opacity is itt szerepel: ez a szabály felülírja a v-reveal globális
     transition-jét (assets/main.css), különben a beúszás villanna. */
  transition: transform .5s cubic-bezier(.22, .61, .36, 1), opacity .7s ease;
}
.machinery-card:hover {
  transform: translateY(-6px);
}

.card-accent {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 4px;
  background: linear-gradient(to right, #fa4002, #fa8f02);
  transform: scaleX(0);
  transform-origin: left;
  transition: transform .5s cubic-bezier(.22, .61, .36, 1);
}
.machinery-card:hover .card-accent {
  transform: scaleX(1);
}

/* Halvány narancs ikonkeret, hoverre töltődik ki. */
.icon-badge {
  background: rgba(255, 118, 24, .1);
  transition: background-color .3s ease, transform .3s cubic-bezier(.22, .61, .36, 1);
}
.machinery-card:hover .icon-badge {
  background: rgb(255, 118, 24);
  transform: rotate(-4deg) scale(1.05);
}

@media (prefers-reduced-motion: reduce) {
  .machinery-card,
  .machinery-card:hover,
  .card-accent,
  .icon-badge,
  .machinery-card:hover .icon-badge {
    transform: none;
    transition: none;
  }
  .machinery-card:hover .card-accent {
    transform: scaleX(1);
  }
}
</style>
